import {Button} from '@/components/ui/button/Button'
import {PrinterIcon} from '@/components/ui/icon/Icon'
import {ModalOverlay} from '@/components/ui/modal-atoms/ModalAtoms'
import {formatDate} from '@/dayjs'
import {MedicalRecordAggregatedPrintFinderModal} from '@/features/medical-records/components/medical-record-aggregated-print-modal/MedicalRecordAggregatedPrintFinderModal'
import {MedicalRecordAggregatedPrintViewerModal} from '@/features/medical-records/components/medical-record-aggregated-print-modal/MedicalRecordAggregatedPrintViewerModal'
import {useGetMedicalRecordAggregatePrints} from '@/features/medical-records/queries/useGetMedicalRecordAggregatePrints'
import {MedicalRecordAggregatedPrint} from '@/features/medical-records/types'
import {MedicalReportDateByProject, MedicalReportTypeByProject} from '@/features/medical-reports/types'
import {UserByPatient} from '@/features/users/types'
import {useToggle} from '@/hooks/useToggle'
import {useTranslation} from '@/translations/i18n'
import {useState} from 'react'

export const MedicalRecordAggregatedPrintModal = ({patientId}: {patientId: string}) => {
    const {t} = useTranslation()
    const {isActive: isModalOpen, on: openModal, off: closeModal} = useToggle()

    const [medicalRecordAggregatePrints, setMedicalRecordAggregatePrints] =
        useState<MedicalRecordAggregatedPrint | null>(null)

    const {mutate: getMedicalRecordAggregatePrints, isPending: isLoading} = useGetMedicalRecordAggregatePrints({
        onSuccess: response => {
            if (!response) return
            setMedicalRecordAggregatePrints(response)
        }
    })

    const retrieveMedicalRecordAggregatePrints = (
        user: UserByPatient,
        reportType: MedicalReportTypeByProject,
        date: MedicalReportDateByProject['date']
    ) => {
        getMedicalRecordAggregatePrints({
            patientId,
            userId: user.id,
            medicalRecordReportTypeId: reportType.id,
            date: formatDate(date, 'YYYY-MM-DD')
        })
    }

    const handleCloseModal = () => {
        setMedicalRecordAggregatePrints(null)
        closeModal()
    }

    return (
        <>
            <Button variant="secondary" onClick={openModal} style={{flexShrink: 0}}>
                <PrinterIcon />
                {t('medical_records:aggregate_reports')}
            </Button>
            {isModalOpen && (
                <>
                    <ModalOverlay
                        onClick={event => {
                            event.stopPropagation()
                            handleCloseModal()
                        }}
                    />
                    {!medicalRecordAggregatePrints ? (
                        <MedicalRecordAggregatedPrintFinderModal
                            patientId={patientId}
                            closeModal={handleCloseModal}
                            isLoading={isLoading}
                            onConfirm={retrieveMedicalRecordAggregatePrints}
                        />
                    ) : (
                        <MedicalRecordAggregatedPrintViewerModal
                            closeModal={handleCloseModal}
                            date={medicalRecordAggregatePrints.date ?? ''}
                            user={medicalRecordAggregatePrints.user}
                            pdfAttachmentUrl={medicalRecordAggregatePrints.attachment?.url ?? ''}
                        />
                    )}
                </>
            )}
        </>
    )
}
